import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Briefcase, Award, Globe, BookOpen, User, MapPin } from 'lucide-react';

const Professor: React.FC = () => {
  return (
    <div className="container mx-auto px-6 max-w-6xl space-y-20 pt-32">
      {/* Header Section */}
      <section className="flex flex-col md:flex-row gap-12 items-start">
        <div className="w-full md:w-80 aspect-[3/4] rounded-[3rem] bg-[var(--accent-blue-soft)] flex-shrink-0 overflow-hidden transition-theme shadow-xl border border-[var(--card-border)] relative group">
          <img 
            src="/images/ganesan.png" 
            alt="Dr. A.R. Ganesan" 
            className="w-full h-full object-cover hover:grayscale-0 transition-all duration-500" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
        
        <div className="flex-grow space-y-6">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-main)] mb-2 transition-theme">
              Dr. A.R. Ganesan
            </h1>
            <p className="text-xl text-[var(--accent-blue)] font-semibold mb-4 transition-theme">
              Professor of Applied Optics
            </p>
            <div className="flex flex-col gap-2 text-[var(--text-muted)] font-medium transition-theme">
              <span className="flex items-center gap-2 text-sm"><MapPin size={16} /> Department of Physics, IIT Madras</span>
              <span className="flex items-center gap-2 text-sm"><Globe size={16} /> Chennai, India</span>
            </div>
          </motion.div>

          <div className="glass-card p-8 rounded-[2rem] border-l-4 ">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-[var(--text-main)]">
              <User size={20} className="text-[var(--accent-blue)]" /> Bio-sketch
            </h3>
            <p className="text-[var(--text-muted)] leading-relaxed text-sm md:text-base">
              Dr. A.R. Ganesan received his Ph.D. from the Indian Institute of Technology Madras, specializing in adaptive optics and optical instrumentation. Over the last three decades, he has contributed significantly to India's precision optics capabilities, especially in the areas of wavefront sensing, optical alignment systems, and photonic platforms. He has held visiting scientist positions at CREOL (USA) and the University of New Mexico, collaborating on atomic sensors and laser stability research. As a faculty member at IIT Madras, he has led multiple national projects funded by DRDO, ISRO, and DST. His work spans both fundamental research and industrial consulting.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Education Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-[var(--text-main)] transition-theme">
            <GraduationCap className="text-[var(--accent-blue)]" /> Education
          </h2>
          <div className="space-y-4">
            <div className="glass-card p-6 rounded-2xl transition-theme">
              <p className="font-bold text-[var(--text-main)]">Ph.D. in Physics</p>
              <p className="text-sm text-[var(--accent-blue)] font-medium mb-2">Applied Optics, Holography, Laser Instrumentation</p>
              <p className="text-sm text-[var(--text-muted)]">IIT Madras, 1989</p>
            </div>
            <div className="glass-card p-6 rounded-2xl transition-theme">
              <p className="font-bold text-[var(--text-main)]">M.Sc. in Physics</p>
              <p className="text-sm text-[var(--accent-blue)] font-medium mb-2">Electronics Specialization</p>
              <p className="text-sm text-[var(--text-muted)]">University of Madras, 1984 – First Class</p>
            </div>
          </div>
        </div>

        {/* Awards & Honors */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-[var(--text-main)] transition-theme">
            <Award className="text-[var(--accent-blue)]" /> Awards and Honors
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {[
              "Alexander von Humboldt Fellowship (Germany)",
              "Distinguished Fellow of the Optical Society of India",
              "Senior Member of SPIE (USA)"
            ].map((award, i) => (
              <div key={i} className="flex items-center gap-4 glass-card p-4 rounded-2xl border-l-4 transition-theme">
                <span className="text-[var(--text-main)] font-medium text-sm">{award}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employment Timeline */}
      <section className="space-y-8">
        <h2 className="text-2xl font-bold flex items-center gap-3 text-[var(--text-main)] transition-theme">
          <Briefcase className="text-[var(--accent-blue)]" /> Experience
        </h2>
        <div className="space-y-6 border-l-2 border-[var(--accent-blue-soft)] ml-4 pl-8 transition-theme">
          {[
            { role: "Professor", org: "Department of Physics, IIT Madras", period: "Mar. 2018 – Present" },
            { role: "Associate Professor", org: "Department of Physics, IIT Madras", period: "Jun. 2006 – Mar. 2018" },
            { role: "Associate Professor", org: "Department of Physics, NIT Tiruchirappalli", period: "Jan. 2005 – Jun. 2006" },
            { role: "Scientist", org: "IRDE, DRDO, Dehradun", period: "Oct. 1994 – Jul. 1995" },
            { role: "Alexander von Humboldt Fellow", org: "University of Oldenburg, Germany", period: "May 1992 – Mar. 1994" },
            { role: "Research Assistant Professor", org: "Alabama A&M University, USA", period: "May 1991 – Apr. 1992" },
            { role: "Postdoctoral Fellow", org: "University of Malaya, Malaysia", period: "Oct. 1989 – Apr. 1991" }
          ].map((job, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-[var(--accent-blue)] border-4 border-[var(--bg-main)]"></div>
              <p className="font-bold text-[var(--text-main)]">{job.role}</p>
              <p className="text-sm text-[var(--accent-blue)] font-medium">{job.org}</p>
              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mt-1">{job.period}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Academic Visits */}
        <section className="space-y-8">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-[var(--text-main)] transition-theme">
            <Globe className="text-[var(--accent-blue)]" /> Academic Visits
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {[
              { place: "Swiss Federal Institute of Technology", city: "Lausanne, Switzerland", period: "Dec. 1990" },
              { place: "Blackett Laboratory, Imperial College", city: "London, UK", period: "Jun. 1993" },
              { place: "Norwegian Institute of Technology", city: "Trondheim, Norway", period: "Sep. 1993" },
              { place: "University of Oldenburg", city: "Germany", period: "Jun. – Aug. 1999" },
              { place: "National University of Ireland", city: "Galway", period: "May – Jun. 2012" }
            ].map((visit, i) => (
              <div key={i} className="glass-card p-6 rounded-2xl transition-theme">
                <p className="font-bold text-[var(--text-main)] text-sm">{visit.place}</p>
                <p className="text-xs text-[var(--accent-blue)] mb-1">{visit.city}</p>
                <p className="text-xs text-[var(--text-muted)] italic">{visit.period}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Editorial & Reviewer */}
        <section className="space-y-8">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-[var(--text-main)] transition-theme">
            <BookOpen className="text-[var(--accent-blue)]" /> Editorial & Reviewer
          </h2>
          <div className="glass-card p-8 rounded-[2rem] space-y-6 transition-theme">
            <div>
              <p className="text-xs font-bold text-[var(--accent-blue)] uppercase tracking-widest mb-4">Journals</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-[var(--text-muted)]">
                {[
                  "Scientific Reports (Nature)",
                  "IEEE Transactions on Magnetics",
                  "J. Magnetism and Magnetic Materials",
                  "Optical Engineering",
                  "Optics & Lasers in Engineering",
                  "Journal of Biomedical Optics",
                  "Optics Communications",
                  "Journal of Measurement Science"
                ].map((journal, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)]"></div>
                    {journal}
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-6 border-t border-[var(--card-border)]">
              <p className="text-xs font-bold text-[var(--accent-blue)] uppercase tracking-widest mb-4">Board Memberships</p>
              <div className="p-4 rounded-xl bg-[var(--accent-blue-soft)]/20 text-sm text-[var(--text-main)] font-medium">
                Editorial Board Member – International Journal of Holography and Speckle
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Professor;